import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router";
import { motion, AnimatePresence } from "framer-motion";
import {
  ClipboardList,
  ChevronDown,
  Home,
  LogOut,
  MapPin,
  User,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../store/slices/userSlice";
import  showToast from "../../utils/toast";

function useUserDisplay(user) {
  return useMemo(() => {
    const fromParts = [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim();
    const fromFull = String(user?.full_name || user?.name || "").trim();
    const displayName = fromParts || fromFull || user?.email?.split("@")[0] || "Member";

    let first = (user?.first_name || "").trim()[0];
    let last = (user?.last_name || "").trim()[0];
    const words = fromFull.split(/\s+/).filter(Boolean);
    if (!first && words[0]) first = words[0][0];
    if (!last && words.length > 1) last = words[words.length - 1][0];
    else if (!last && words.length === 1 && words[0].length > 1) last = words[0][1];

    const emailLocal = String(user?.email?.split("@")[0] || "");
    const initials =
      first && last
        ? `${first}${last}`.toUpperCase()
        : first && emailLocal[1]
          ? `${first}${emailLocal[1]}`.toUpperCase()
          : (emailLocal.slice(0, 2).toUpperCase() || "IB");

    return { displayName, initials };
  }, [user]);
}

/** Desktop: trigger + floating menu (use inside dark navbar). */
export function AccountNavDropdown() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((s) => s.infoboxx_consumer);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const { displayName, initials } = useUserDisplay(user);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    const onDown = (e) => {
      if (!wrapRef.current?.contains(e.target)) close();
    };
    const onEsc = (e) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [close]);

  const handleLogout = () => {
    dispatch(logout());
    showToast.success("Signed out");
    navigate("/");
    close();
  };

  return (
    <div ref={wrapRef} className="relative">
      <motion.button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.08] py-1 pl-1 pr-2.5 text-left shadow-sm backdrop-blur-sm transition hover:border-[#FFD221]/40 hover:bg-white/[0.12]"
        whileTap={{ scale: 0.98 }}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-black text-black shadow-inner ring-2 ring-black/10" style={{ background: "#FFD221" }}>
          {initials}
        </span>
        <span className="hidden min-w-0 max-w-[200px] truncate text-sm font-semibold text-gray-900 sm:block">
          {displayName}
        </span>
        <ChevronDown
          size={16}
          className={`shrink-0 text-gray-500 transition-transform ${open ? "rotate-180" : ""}`}
          aria-hidden
        />
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            role="menu"
            initial={{ opacity: 0, y: 8, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.97 }}
            transition={{ duration: 0.18, ease: "easeOut" }}
            className="absolute right-0 z-[120] mt-2 w-[min(calc(100vw-2rem),320px)] overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-[0_20px_50px_-12px_rgba(0,0,0,0.25)]"
          >
            <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
              <p className="text-sm font-semibold truncate text-slate-900">{displayName}</p>
              <p className="text-xs truncate text-slate-500">{user?.email}</p>
              {user?.location ? (
                <p className="mt-1.5 flex items-start gap-1.5 text-[11px] leading-snug text-slate-600">
                  <MapPin size={12} className="mt-0.5 shrink-0" style={{ color: "#E5B800" }} />
                  <span className="line-clamp-2">{user.location}</span>
                </p>
              ) : null}
            </div>

            <div className="bg-white py-1.5">
              <Link
                to="/"
                role="menuitem"
                onClick={close}
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-800 transition hover:bg-slate-100"
              >
                <Home size={18} style={{ color: "#E5B800" }} strokeWidth={2} />
                Home
              </Link>
              <Link
                to="/profile"
                role="menuitem"
                onClick={close}
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-800 transition hover:bg-slate-100"
              >
                <User size={18} style={{ color: "#E5B800" }} strokeWidth={2} />
                Profile
              </Link>
              <Link
                to="/applications"
                role="menuitem"
                onClick={close}
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-800 transition hover:bg-slate-100"
              >
                <ClipboardList size={18} style={{ color: "#E5B800" }} strokeWidth={2} />
                Applications
              </Link>
              {/* <Link
                to="/settings"
                role="menuitem"
                onClick={close}
                className="flex items-center gap-3 px-4 py-2.5 text-sm text-slate-800 transition hover:bg-slate-100"
              >
                <Settings size={18} className="text-[#34846E]" strokeWidth={2} />
                Settings
              </Link> */}
            </div>

            <div className="border-t border-slate-100 bg-white p-1.5">
              <button
                type="button"
                role="menuitem"
                onClick={handleLogout}
                className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-red-600 transition hover:bg-red-50 hover:text-red-700"
              >
                <LogOut size={18} strokeWidth={2} />
                Sign out
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/** Mobile drawer section: user card + actions */
export function AccountNavMobileSection({ onClose }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((s) => s.infoboxx_consumer);
  const { displayName, initials } = useUserDisplay(user);

  const handleLogout = () => {
    dispatch(logout());
    showToast.success("Signed out");
    onClose?.();
    navigate("/");
  };

  return (
    <div className="pt-3 mt-3 space-y-2 border-t border-white/10">
      <div className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.06] px-3 py-3">
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-black text-black" style={{ background: "#FFD221" }}>
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-white truncate">{displayName}</p>
          <p className="text-xs truncate text-white/55">{user?.email}</p>
        </div>
      </div>
      <Link
        to="/"
        onClick={onClose}
        className="flex gap-3 items-center px-4 py-3 text-sm font-medium rounded-xl transition-colors text-white/90 hover:bg-white/10"
      >
        <Home size={18} className="text-[#E5B80B]" />
        Home
      </Link>
      <Link
        to="/profile"
        onClick={onClose}
        className="flex gap-3 items-center px-4 py-3 text-sm font-medium rounded-xl transition-colors text-white/90 hover:bg-white/10"
      >
        <User size={18} className="text-[#E5B80B]" />
        Profile
      </Link>
      <Link
        to="/applications"
        onClick={onClose}
        className="flex gap-3 items-center px-4 py-3 text-sm font-medium rounded-xl transition-colors text-white/90 hover:bg-white/10"
      >
        <ClipboardList size={18} className="text-[#E5B80B]" />
        Applications
      </Link>
      {/* <Link
        to="/settings"
        onClick={onClose}
        className="flex gap-3 items-center px-4 py-3 text-sm font-medium rounded-xl transition-colors text-white/90 hover:bg-white/10"
      >
        <Settings size={18} className="text-[#E5B80B]" />
        Settings
      </Link> */}
      <button
        type="button"
        onClick={handleLogout}
        className="flex gap-3 items-center px-4 py-3 w-full text-sm font-medium text-left text-red-300 rounded-xl transition-colors hover:bg-red-500/15"
      >
        <LogOut size={18} />
        Sign out
      </button>
    </div>
  );
}

export default AccountNavDropdown;
